import React, { useState, useEffect } from 'react';
import {
  Building2,
  ChevronRight,
  Copy,
  FileText,
  Folder,
  FolderInput,
  FolderPlus,
  Lock,
  Mail,
  Pencil,
  Share2,
  ShieldAlert,
  Trash2,
  Unlock,
  User,
  Users,
} from 'lucide-react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { CheckIcon, SpinnerIcon, XIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import TemplateFolderDeleteConfirmModal from './TemplateFolderDeleteConfirmModal';
import TemplateFolderCopyConfirmModal from './TemplateFolderCopyConfirmModal';
import type { EmailTemplateFolder } from '../useData';

type FolderTreeNode = EmailTemplateFolder & { children: FolderTreeNode[] };

const SCOPE_STYLES = {
  COMPANY: {
    badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300',
    iconWell: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400',
    label: 'Company',
    ScopeIcon: Building2,
  },
  PERSONAL: {
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
    iconWell: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400',
    label: 'Personal',
    ScopeIcon: User,
  },
  SHARED: {
    badge: 'bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-300',
    iconWell: 'bg-violet-100 text-violet-600 dark:bg-violet-950/60 dark:text-violet-400',
    label: 'Shared',
    ScopeIcon: Users,
  },
} as const;

function collectDescendantIds(node: FolderTreeNode): Set<number> {
  const set = new Set<number>([node.id]);
  node.children.forEach((c) => collectDescendantIds(c).forEach((id) => set.add(id)));
  return set;
}

function flattenForMovePicker(
  nodes: FolderTreeNode[],
  excludeIds: Set<number>,
  depth = 0,
): { id: number; name: string; depth: number }[] {
  const out: { id: number; name: string; depth: number }[] = [];
  for (const node of nodes) {
    if (excludeIds.has(node.id)) continue;
    out.push({ id: node.id, name: node.name, depth });
    if (node.children.length) {
      out.push(...flattenForMovePicker(node.children, excludeIds, depth + 1));
    }
  }
  return out;
}

function findCopyTargetName(
  sections: { label: string; options: { id: number; name: string; depth: number }[] }[],
  targetId: number,
): string | null {
  for (const section of sections) {
    const match = section.options.find((option) => option.id === targetId);
    if (match) return match.name;
  }
  return null;
}

function flattenCopyDestinations(
  companyTree: FolderTreeNode[],
  personalTree: FolderTreeNode[],
  excludeIds: Set<number>,
): { label: string; options: { id: number; name: string; depth: number }[] }[] {
  const sections: { label: string; options: { id: number; name: string; depth: number }[] }[] = [];
  const companyOptions = flattenForMovePicker(companyTree, excludeIds, 0);
  const personalOptions = flattenForMovePicker(personalTree, excludeIds, 0);
  if (companyOptions.length > 0) {
    sections.push({ label: 'Company', options: companyOptions });
  }
  if (personalOptions.length > 0) {
    sections.push({ label: 'Personal', options: personalOptions });
  }
  return sections;
}

function StatChip({
  icon: Icon,
  value,
  label,
  colors,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  label: string;
  colors: ReturnType<typeof useTheme>['colors'];
  tone: string;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3 py-2',
        colors.border,
        'bg-white/70 dark:bg-gray-900/30',
      )}
    >
      <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-md', tone)}>
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <p className={cn('text-sm font-semibold leading-none', colors.text)}>{value}</p>
        <p className={cn('text-[11px] mt-0.5 truncate', colors.textMuted)}>{label}</p>
      </div>
    </div>
  );
}

function ActionRow({
  icon: Icon,
  title,
  description,
  onClick,
  disabled,
  iconTone,
  colors,
  showChevron = true,
  borderTop = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  onClick: () => void;
  disabled?: boolean;
  iconTone: string;
  colors: ReturnType<typeof useTheme>['colors'];
  showChevron?: boolean;
  borderTop?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors disabled:opacity-50',
        borderTop && cn('border-t', colors.border),
        colors.text,
        'hover:bg-gray-50 dark:hover:bg-gray-800/60',
      )}
    >
      <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', iconTone)}>
        <Icon className="h-[18px] w-[18px]" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        {description ? <p className={cn('text-xs mt-0.5', colors.textMuted)}>{description}</p> : null}
      </div>
      {showChevron ? <ChevronRight className={cn('h-4 w-4 shrink-0 opacity-40', colors.textMuted)} aria-hidden /> : null}
    </button>
  );
}

function FolderPickerPanel({
  title,
  options,
  sections,
  saving,
  onSelect,
  onCancel,
  colors,
}: {
  title: string;
  options?: { id: number; name: string; depth: number }[];
  sections?: { label: string; options: { id: number; name: string; depth: number }[] }[];
  saving: boolean;
  onSelect: (parentId: number) => void;
  onCancel: () => void;
  colors: ReturnType<typeof useTheme>['colors'];
}) {
  const flatOptions = options ?? [];
  const groupedSections = sections ?? [];
  const hasSections = groupedSections.length > 0;
  const optionCount = hasSections
    ? groupedSections.reduce((sum, section) => sum + section.options.length, 0)
    : flatOptions.length;

  const renderFolderButton = (f: { id: number; name: string; depth: number }) => (
    <button
      key={f.id}
      type="button"
      onClick={() => onSelect(f.id)}
      disabled={saving}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
        colors.text,
        'hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50',
      )}
      style={{ paddingLeft: 12 + f.depth * 16 }}
    >
      <Folder className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
      <span className="truncate">{f.name}</span>
    </button>
  );

  return (
    <div className={cn('border-t p-3', colors.border, 'bg-gray-50/80 dark:bg-gray-900/40')}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>{title}</p>
        <button
          type="button"
          onClick={onCancel}
          className={cn('rounded-md p-1 text-xs font-medium', colors.textMuted, colors.interactiveHover)}
        >
          Cancel
        </button>
      </div>
      <div className="max-h-40 overflow-y-auto rounded-lg border bg-white dark:bg-gray-950/50 space-y-0.5 p-1 scrollbar-thin">
        {optionCount === 0 ? (
          <p className={cn('px-3 py-2 text-sm', colors.textMuted)}>No destination folders available.</p>
        ) : hasSections ? (
          groupedSections.map((section) => (
            <div key={section.label} className="space-y-0.5">
              <p
                className={cn(
                  'sticky top-0 z-[1] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider',
                  colors.textMuted,
                  'bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm',
                )}
              >
                {section.label}
              </p>
              {section.options.map((f) => renderFolderButton(f))}
            </div>
          ))
        ) : (
          flatOptions.map((f) => renderFolderButton(f))
        )}
      </div>
    </div>
  );
}

export interface TemplateFolderOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: FolderTreeNode | null;
  scope: 'PERSONAL' | 'COMPANY' | 'SHARED';
  folderTree: FolderTreeNode[];
  /** Company + Personal trees for copy destinations. */
  copyDestinationTrees?: { company: FolderTreeNode[]; personal: FolderTreeNode[] };
  copyFolderTree?: FolderTreeNode[];
  templateCount: number;
  subtreeSubfolderCount: number;
  documentCount: number;
  subfolderCount: number;
  directTemplateCount: number;
  canEdit: boolean;
  isPathLocked?: boolean;
  hasLockedAncestor?: boolean;
  isSystemRoot?: boolean;
  ownerName?: string;
  onShare?: (folder: { id: number; name: string }) => void;
  onRename: (folderId: number, name: string) => Promise<void>;
  onMove: (folderId: number, parentId: number | null) => Promise<void>;
  onCopy?: (folderId: number, parentId: number) => Promise<void>;
  onToggleLock?: (folderId: number, locked: boolean) => Promise<void>;
  onAddSubfolder?: (folder: FolderTreeNode) => void;
  onDelete: (folderId: number) => Promise<void>;
}

const TemplateFolderOptionsModal: React.FC<TemplateFolderOptionsModalProps> = ({
  isOpen,
  onClose,
  folder,
  scope,
  folderTree,
  copyDestinationTrees,
  copyFolderTree,
  templateCount,
  subtreeSubfolderCount,
  documentCount,
  subfolderCount,
  directTemplateCount,
  canEdit,
  isPathLocked = false,
  hasLockedAncestor = false,
  isSystemRoot = false,
  ownerName,
  onShare,
  onRename,
  onMove,
  onCopy,
  onToggleLock,
  onAddSubfolder,
  onDelete,
}) => {
  const { colors } = useTheme();
  const scopeStyle = SCOPE_STYLES[scope];
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingLock, setTogglingLock] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [showMovePicker, setShowMovePicker] = useState(false);
  const [showCopyPicker, setShowCopyPicker] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [pendingCopyTargetId, setPendingCopyTargetId] = useState<number | null>(null);
  const [pendingCopyTargetName, setPendingCopyTargetName] = useState('');
  const [savingMove, setSavingMove] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (folder && isOpen) {
      setRenaming(false);
      setRenameValue(folder.name);
      setShowMovePicker(false);
      setShowCopyPicker(false);
      setShowCopyConfirm(false);
      setPendingCopyTargetId(null);
      setPendingCopyTargetName('');
    }
  }, [folder, isOpen]);

  const handleShare = () => {
    if (folder && onShare) onShare({ id: folder.id, name: folder.name });
    onClose();
  };

  const handleDeleteConfirm = async () => {
    if (!folder) return;
    setDeleting(true);
    setShowDeleteConfirm(false);
    onClose();
    try {
      await onDelete(folder.id);
    } catch {
      /**/
    } finally {
      setDeleting(false);
    }
  };

  const startRename = () => {
    if (folder) {
      setRenameValue(folder.name);
      setRenaming(true);
    }
  };

  const saveRename = async () => {
    if (!folder || !renameValue.trim()) return;
    setSavingRename(true);
    try {
      await onRename(folder.id, renameValue.trim());
      setRenaming(false);
    } finally {
      setSavingRename(false);
    }
  };

  const cancelRename = () => {
    setRenameValue(folder?.name ?? '');
    setRenaming(false);
  };

  const moveOptions = folder ? flattenForMovePicker(folderTree, collectDescendantIds(folder)) : [];
  const copySections = folder
    ? copyDestinationTrees
      ? flattenCopyDestinations(
          copyDestinationTrees.company,
          copyDestinationTrees.personal,
          collectDescendantIds(folder),
        )
      : [
          {
            label: scope === 'COMPANY' ? 'Company' : scope === 'PERSONAL' ? 'Personal' : 'Shared',
            options: flattenForMovePicker(copyFolderTree ?? folderTree, collectDescendantIds(folder)),
          },
        ]
    : [];
  const canOrganize = canEdit && !isSystemRoot && !isPathLocked;
  const canCopy = Boolean(onCopy) && !isSystemRoot;
  const folderLocked = folder?.isLocked === true;
  const canLockFolder = Boolean(onToggleLock) && canEdit && !isSystemRoot && !isPathLocked;
  const canUnlockFolder =
    Boolean(onToggleLock) && canEdit && !isSystemRoot && folderLocked && !hasLockedAncestor;
  const showOrganizeSection = canOrganize || canCopy || (canEdit && isSystemRoot && Boolean(onAddSubfolder));

  const handleMoveSelect = async (parentId: number) => {
    if (!folder) return;
    setSavingMove(true);
    try {
      await onMove(folder.id, parentId);
      setShowMovePicker(false);
      onClose();
    } finally {
      setSavingMove(false);
    }
  };

  const handleCopySelect = (parentId: number) => {
    const targetName = findCopyTargetName(copySections, parentId) ?? 'Destination';
    setPendingCopyTargetId(parentId);
    setPendingCopyTargetName(targetName);
    setShowCopyPicker(false);
    setShowCopyConfirm(true);
  };

  const handleCopyConfirm = async () => {
    if (!folder || !onCopy || pendingCopyTargetId == null) return;
    setCopying(true);
    setShowCopyConfirm(false);
    onClose();
    try {
      await onCopy(folder.id, pendingCopyTargetId);
    } catch {
      /**/
    } finally {
      setCopying(false);
      setPendingCopyTargetId(null);
      setPendingCopyTargetName('');
    }
  };

  const handleToggleLock = async () => {
    if (!folder || !onToggleLock) return;
    setTogglingLock(true);
    try {
      await onToggleLock(folder.id, !folderLocked);
      onClose();
    } finally {
      setTogglingLock(false);
    }
  };

  if (!folder) return null;

  const deleteSummaryParts: string[] = [];
  if (subtreeSubfolderCount > 0) {
    deleteSummaryParts.push(
      `${subtreeSubfolderCount} subfolder${subtreeSubfolderCount === 1 ? '' : 's'}`,
    );
  }
  if (templateCount > 0) {
    deleteSummaryParts.push(`${templateCount} template${templateCount === 1 ? '' : 's'}`);
  }
  if (documentCount > 0) {
    deleteSummaryParts.push(`${documentCount} document${documentCount === 1 ? '' : 's'}`);
  }
  const copySummaryParts = deleteSummaryParts;

  const ScopeIcon = scopeStyle.ScopeIcon;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalHeader>
          <ModalTitle>Folder options</ModalTitle>
          <button
            type="button"
            onClick={onClose}
            className={cn('rounded-lg p-1.5', colors.textMuted, colors.interactiveHover)}
            aria-label="Close"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4 pb-1">
          {/* Folder identity */}
          <div className={cn('overflow-hidden rounded-2xl border', colors.border, 'bg-gradient-to-br from-gray-50/90 to-white dark:from-gray-900/50 dark:to-gray-950/30')}>
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl', scopeStyle.iconWell)}>
                  <Folder className="h-6 w-6" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  {renaming ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveRename();
                          if (e.key === 'Escape') cancelRename();
                        }}
                        maxLength={255}
                        autoFocus
                        className={cn(
                          'w-full rounded-lg border px-3 py-2 text-sm font-semibold',
                          colors.background,
                          colors.border,
                          'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                        )}
                        aria-label="Folder name"
                      />
                      <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={cancelRename} disabled={savingRename}>
                          Cancel
                        </Button>
                        <Button type="button" size="sm" onClick={() => void saveRename()} disabled={savingRename || !renameValue.trim()}>
                          {savingRename ? <SpinnerIcon className="h-4 w-4" /> : 'Save'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        <p className={cn('min-w-0 flex-1 text-base font-semibold leading-snug break-words', colors.text)} title={folder.name}>
                          {folder.name}
                        </p>
                        {canOrganize ? (
                          <button
                            type="button"
                            onClick={startRename}
                            className={cn('rounded-lg p-1.5 shrink-0', colors.textMuted, colors.interactiveHover)}
                            title="Rename folder"
                            aria-label="Rename folder"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold', scopeStyle.badge)}>
                          <ScopeIcon className="h-3 w-3" aria-hidden />
                          {scopeStyle.label}
                        </span>
                        {isSystemRoot ? (
                          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', colors.textMuted, 'bg-gray-100 dark:bg-gray-800')}>
                            Root
                          </span>
                        ) : null}
                        {isPathLocked ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                            <Lock className="h-3 w-3" aria-hidden />
                            Locked
                          </span>
                        ) : null}
                        {scope === 'SHARED' && !canEdit ? (
                          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', colors.textMuted, 'bg-gray-100 dark:bg-gray-800')}>
                            View only
                          </span>
                        ) : null}
                      </div>
                      {ownerName ? (
                        <p className={cn('mt-1.5 text-xs', colors.textMuted)}>Shared by {ownerName}</p>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>

            {!isSystemRoot ? (
              <div className={cn('grid grid-cols-3 gap-2 border-t px-3 py-3', colors.border)}>
                <StatChip
                  icon={Mail}
                  value={templateCount}
                  label={templateCount === 1 ? 'Template' : 'Templates'}
                  colors={colors}
                  tone="bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400"
                />
                <StatChip
                  icon={Folder}
                  value={subfolderCount}
                  label={subfolderCount === 1 ? 'Subfolder' : 'Subfolders'}
                  colors={colors}
                  tone="bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                />
                <StatChip
                  icon={FileText}
                  value={documentCount}
                  label={documentCount === 1 ? 'Document' : 'Documents'}
                  colors={colors}
                  tone="bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-400"
                />
              </div>
            ) : null}
          </div>

          {isPathLocked ? (
            <div
              className={cn(
                'flex gap-3 rounded-xl border px-4 py-3',
                'border-amber-200 bg-amber-50/90 dark:border-amber-900/50 dark:bg-amber-950/25',
              )}
            >
              <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" aria-hidden />
              <p className="text-sm text-amber-900 dark:text-amber-100/90">
                {hasLockedAncestor
                  ? 'A parent folder is locked. This folder and everything inside it is read-only until the parent is unlocked.'
                  : 'This folder and all subfolders and files inside it are locked. Changes are blocked until you unlock it.'}
              </p>
            </div>
          ) : null}

          {showOrganizeSection ? (
            <div className={cn('overflow-hidden rounded-xl border', colors.border)}>
              <p className={cn('px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                {isSystemRoot ? 'Folders' : 'Organize'}
              </p>
              {canOrganize && showMovePicker ? (
                <FolderPickerPanel
                  title="Move to"
                  options={moveOptions}
                  saving={savingMove}
                  onSelect={(id) => void handleMoveSelect(id)}
                  onCancel={() => setShowMovePicker(false)}
                  colors={colors}
                />
              ) : canCopy && showCopyPicker ? (
                <FolderPickerPanel
                  title="Copy to"
                  sections={copySections}
                  saving={copying}
                  onSelect={(id) => handleCopySelect(id)}
                  onCancel={() => setShowCopyPicker(false)}
                  colors={colors}
                />
              ) : (
                <>
                  {canOrganize ? (
                    <ActionRow
                      icon={FolderInput}
                      title="Move to…"
                      description="Relocate this folder in the library"
                      onClick={() => {
                        setShowCopyPicker(false);
                        setShowMovePicker(true);
                      }}
                      iconTone="bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-400"
                      colors={colors}
                    />
                  ) : null}
                  {canCopy ? (
                    <ActionRow
                      icon={Copy}
                      title="Copy to…"
                      description="Duplicate into Company or Personal library"
                      onClick={() => {
                        setShowMovePicker(false);
                        setShowCopyPicker(true);
                      }}
                      borderTop={canOrganize}
                      iconTone="bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400"
                      colors={colors}
                    />
                  ) : null}
                  {onAddSubfolder && (!canOrganize || (!showMovePicker && !showCopyPicker)) ? (
                    <ActionRow
                      icon={FolderPlus}
                      title="Add subfolder"
                      description={
                        isSystemRoot
                          ? 'Create a folder inside this library'
                          : `${directTemplateCount} template${directTemplateCount === 1 ? '' : 's'} in this folder`
                      }
                      onClick={() => {
                        onAddSubfolder(folder);
                        onClose();
                      }}
                      borderTop={canOrganize || canCopy}
                      iconTone="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400"
                      colors={colors}
                    />
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {scope === 'PERSONAL' && onShare && canOrganize ? (
            <div className={cn('overflow-hidden rounded-xl border', colors.border)}>
              <ActionRow
                icon={Share2}
                title="Share folder"
                description="Give others read or write access"
                onClick={handleShare}
                iconTone="bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400"
                colors={colors}
                showChevron={false}
              />
            </div>
          ) : null}

          {(canLockFolder || canUnlockFolder) ? (
            <div
              className={cn(
                'overflow-hidden rounded-xl border',
                folderLocked
                  ? 'border-amber-200 dark:border-amber-900/50'
                  : colors.border,
              )}
            >
              <button
                type="button"
                onClick={() => void handleToggleLock()}
                disabled={togglingLock}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors disabled:opacity-50',
                  folderLocked
                    ? 'bg-amber-50/80 hover:bg-amber-100/80 dark:bg-amber-950/20 dark:hover:bg-amber-950/35'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-800/60',
                )}
              >
                <div
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                    folderLocked
                      ? 'bg-amber-200 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
                  )}
                >
                  {folderLocked ? <Unlock className="h-[18px] w-[18px]" aria-hidden /> : <Lock className="h-[18px] w-[18px]" aria-hidden />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-semibold', folderLocked ? 'text-amber-900 dark:text-amber-100' : colors.text)}>
                    {folderLocked ? 'Unlock folder and contents' : 'Lock folder and contents'}
                  </p>
                  <p className={cn('text-xs mt-0.5', folderLocked ? 'text-amber-800/80 dark:text-amber-200/80' : colors.textMuted)}>
                    {folderLocked
                      ? 'Allow edits, uploads, and deletes again'
                      : 'Prevent changes and deletion for this folder and everything inside'}
                  </p>
                </div>
                {togglingLock ? <SpinnerIcon className="h-4 w-4 shrink-0" /> : null}
              </button>
            </div>
          ) : null}

          {canOrganize ? (
            <div className={cn('overflow-hidden rounded-xl border border-red-200 dark:border-red-900/50')}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors disabled:opacity-50',
                  'bg-red-50/50 hover:bg-red-50 dark:bg-red-950/15 dark:hover:bg-red-950/30',
                )}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400">
                  <Trash2 className="h-[18px] w-[18px]" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-red-700 dark:text-red-300">Delete folder</p>
                  <p className="text-xs mt-0.5 text-red-600/80 dark:text-red-400/80">
                    Permanently remove this folder and all contents
                  </p>
                </div>
              </button>
            </div>
          ) : null}

          {!canEdit ? (
            <p className={cn('text-center text-sm px-2', colors.textMuted)}>
              You have view-only access to this shared folder.
            </p>
          ) : null}
          </div>
        </ModalContent>
      </Modal>

      <TemplateFolderDeleteConfirmModal
        isOpen={showDeleteConfirm}
        folderName={folder.name}
        itemsToRemove={deleteSummaryParts}
        deleting={deleting}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
      />

      <TemplateFolderCopyConfirmModal
        isOpen={showCopyConfirm}
        folderName={folder.name}
        targetFolderName={pendingCopyTargetName}
        itemsToCopy={copySummaryParts}
        copying={copying}
        onClose={() => {
          setShowCopyConfirm(false);
          setPendingCopyTargetId(null);
          setPendingCopyTargetName('');
        }}
        onConfirm={() => void handleCopyConfirm()}
      />
    </>
  );
};

export default TemplateFolderOptionsModal;
